import { useSyncExternalStore } from 'react';
import { ATP_DEFAULT_GLN, type AtpContext, type Values } from './rmisAtp';
import type { RmisAtpResult } from './api';

// One in-memory session shared by every Gendac ATP screen, so the GLN, the
// token and the transfer ids follow the operator from screen to screen. The
// token lives here only: never written to storage, never put in the evidence.

export type AtpLogEntry = {
  at: string;
  screen: string;
  step: string;
  title: string;
  action: string;
  request: unknown;
  requestId: string;
  statusCode: number | null;
  response: unknown;
  error: string;
};

export type AtpStepResult = { result: RmisAtpResult; request: unknown };

export type AtpSession = AtpContext & {
  token: string;
  values: Record<string, Values>;
  results: Record<string, AtpStepResult>;
  confirmed: Record<string, boolean>;
  notes: Record<string, string>;
  log: AtpLogEntry[];
};

const initial = (): AtpSession => ({
  gln: ATP_DEFAULT_GLN,
  token: '',
  transferId: '',
  onwardTransferId: '',
  values: {},
  results: {},
  confirmed: {},
  notes: {},
  log: [],
});

let session: AtpSession = initial();
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

export const getAtpSession = () => session;

export function patchAtpSession(patch: Partial<AtpSession> | ((s: AtpSession) => Partial<AtpSession>)) {
  const next = typeof patch === 'function' ? patch(session) : patch;
  session = { ...session, ...next };
  emit();
}

export function resetAtpSession() {
  session = initial();
  emit();
}

export function useAtpSession(): AtpSession {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getAtpSession,
  );
}
